import React, { useEffect, useRef, useState } from 'react';

const MAX_BYTES = 10 * 1024 * 1024;
const isJpg = (file) => file.type === 'image/jpeg' || /\.jpe?g$/i.test(file.name);

// Drag-and-drop (or click to browse) picker for one JPG. Calls onChange(file | null).
export default function ImageDropZone({ file, onChange }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const jpg = files.find(isJpg);
    if (!jpg) {
      setError('Only JPG images are accepted.');
      return;
    }
    if (jpg.size > MAX_BYTES) {
      setError('That image is larger than 10 MB.');
      return;
    }
    setError(null);
    onChange(jpg); // one image per listing: extra files are ignored
  };

  const openPicker = () => inputRef.current && inputRef.current.click();

  return (
    <div className="rec-dropzone-wrap">
      <div
        className={`rec-dropzone${dragging ? ' rec-dropzone-active' : ''}`}
        role="button"
        tabIndex={0}
        onClick={openPicker}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPicker();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
      >
        {previewUrl ? (
          <img src={previewUrl} alt="Selected listing" />
        ) : (
          <div className="rec-dropzone-hint">
            <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V3m0 0L7.5 7.5M12 3l4.5 4.5M3.75 16.5v2.25A1.5 1.5 0 0 0 5.25 20.25h13.5a1.5 1.5 0 0 0 1.5-1.5V16.5" />
            </svg>
            <span>Drag and drop a JPG here, or click to browse</span>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg"
          hidden
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      <div className="rec-dropzone-footer">
        {file && (
          <>
            <span className="rec-muted rec-sm">{file.name} ({Math.round(file.size / 1024)} KB)</span>
            <button type="button" className="rec-btn" onClick={() => onChange(null)}>Remove</button>
          </>
        )}
      </div>
      {error && <p className="rec-error rec-sm">{error}</p>}
    </div>
  );
}
